import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Activity, Settings, RefreshCw, Zap } from 'lucide-react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

export default function SeoSubscriptions() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('token') || '';
        const res = await fetch(`${API_URL}/brand-projects?system=seo`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setProjects(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch projects', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const getFreqLabel = (freq) => {
    if (freq === 'daily') return <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-md text-xs font-medium">日更 (Daily)</span>;
    if (freq === 'weekly') return <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded-md text-xs font-medium">周更 (Weekly)</span>;
    if (freq === 'monthly') return <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 rounded-md text-xs font-medium">月更 (Monthly)</span>;
    return <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 rounded-md text-xs font-medium">手动更新</span>;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <Calendar className="mr-2" size={24} />
            订阅计划与调度大盘
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            总览各个监控项目的定时抓取频率、引擎消耗情况。
          </p>
        </div>
      </div>

      
      {/* 积分消耗统计栏 */}
      {user?.userGroup && (
        <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">总计配额 (Total Quota)</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{user.userGroup.initialQuota}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Zap className="text-blue-600 dark:text-blue-400" size={20} />
                </div>
            </div>
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">已用配额 (Used Quota)</p>
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">{user.userGroup.quotaUsed}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <Activity className="text-orange-600 dark:text-orange-400" size={20} />
                </div>
            </div>
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">剩余配额 (Remaining)</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{Math.max(0, user.userGroup.initialQuota - user.userGroup.quotaUsed)}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <Zap className="text-green-600 dark:text-green-400" size={20} />
                </div>
            </div>
        </div>
      )}

      {loading ? (

        <div className="flex justify-center py-20">
          <RefreshCw className="animate-spin text-gray-400" size={32} />
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
          <p className="text-gray-500 dark:text-gray-400">暂无任何监控项目配置</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {projects.map(project => {
            const scheduledTasks = project.geoTasks || [];
            let hasDaily = false;
            let hasWeekly = false;
            let hasMonthly = false;

            scheduledTasks.forEach(task => {
                if (task.status !== 'scheduled' && task.status !== 'active') return;
                try {
                    const freq = typeof task.frequency === 'string' ? JSON.parse(task.frequency) : task.frequency;
                    if (freq?.type === 'daily') hasDaily = true;
                    if (freq?.type === 'weekly') hasWeekly = true;
                    if (freq?.type === 'monthly') hasMonthly = true;
                } catch (e) {
                    if (task.frequency === 'daily') hasDaily = true;
                    if (task.frequency === 'weekly') hasWeekly = true;
                    if (task.frequency === 'monthly') hasMonthly = true;
                }
            });

            const isRecurring = hasDaily || hasWeekly || hasMonthly;
            const hasOnce = !isRecurring;
            
            let planStatusLabel = '未配置';
            let planClass = 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400';
            
            if (hasDaily) {
                planStatusLabel = '日更模式 (高频)';
                planClass = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
            } else if (hasWeekly) {
                planStatusLabel = '周更模式 (中频)';
                planClass = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
            } else if (hasMonthly) {
                planStatusLabel = '月更模式 (低频)';
                planClass = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
            } else {
                planStatusLabel = '单次任务 (一次性)';
                planClass = 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
            }

            const activeEngines = project.engineConfigs ? project.engineConfigs.filter(e => e.status === 'active') : [];
            const engineCount = activeEngines.length;
            
            // Calculate actual matrix size
            let keywordsCount = 1;
            if (project.system === 'seo') {
                if (project.seoKeywords) {
                    keywordsCount = project.seoKeywords.length > 0 ? project.seoKeywords.length : 1;
                }
            } else if (project.classifiedKeywords) {
                try {
                    const keywords = JSON.parse(project.classifiedKeywords);
                    keywordsCount = keywords.length > 0 ? keywords.length : 1;
                } catch (e) {
                    console.error("Failed to parse classifiedKeywords", e);
                }
            }
            
            let personasCount = 1;
            if (project.activePersonaIds) {
                const ids = project.activePersonaIds.split(',').filter(id => id.trim() !== '');
                personasCount = ids.length > 0 ? ids.length : 1;
            }
            
            const matrixUnitCost = keywordsCount * personasCount * engineCount; // Defaults to 1 point per matrix unit
            
            const estimatedCost = isRecurring 
                ? (hasDaily ? matrixUnitCost * 30 : hasWeekly ? matrixUnitCost * 4 : matrixUnitCost * 1)
                : matrixUnitCost;

            return (
              <div key={project.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-5 hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-colors relative">
                {/* 任务类型角标 */}
                {isRecurring ? (
                    <div className="absolute top-0 right-0 bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg rounded-tr-xl">
                      周期订阅
                    </div>
                ) : (
                    <div className="absolute top-0 right-0 bg-purple-500 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg rounded-tr-xl">
                      单次抓取
                    </div>
                )}

                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate pr-16">{project.name}{project.subBrandName ? project.subBrandName : ''}</h3>
                </div>

                <div className="space-y-4">
                  {/* (1) 哪些引擎 */}
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-medium">配置引擎</div>
                    {activeEngines.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {activeEngines.map(e => {
                            const iconMap = {
  /* INJECTED_MB_PC_LOGOS_v2 */
  '豆包': '/icons/doubao.png',
  '豆包(PC)': '/icons/doubao.png',
  '豆包(MB)': '/icons/doubao.png',
  '豆包（PC）': '/icons/doubao.png',
  '豆包（MB）': '/icons/doubao.png',
  'doubao': '/icons/doubao.png', 'doubmb': '/icons/doubao.png', 'ybmb': '/icons/yuanbao.png', 'qwmb': '/icons/tongyi.png', 'txyb': '/icons/yuanbao.png', 'tyqw': '/icons/tongyi.png',
  '腾讯元宝(PC)': '/icons/yuanbao.png',
  '腾讯元宝(MB)': '/icons/yuanbao.png',
  '腾讯元宝（PC）': '/icons/yuanbao.png',
  '腾讯元宝（MB）': '/icons/yuanbao.png',
  '通义千问(PC)': '/icons/tongyi.png',
  '通义千问(MB)': '/icons/tongyi.png',
  '通义千问（PC）': '/icons/tongyi.png',
  '通义千问（MB）': '/icons/tongyi.png',
  '通义': '/icons/tongyi.png',
  '文心一言': '/icons/wenxin.png',
  '文心一言(PC)': '/icons/wenxin.png',
  '文心一言(MB)': '/icons/wenxin.png',
  'Deepseek': '/icons/deepseek.png',
  'DeepSeek': '/icons/deepseek.png',
  'deepseek': '/icons/deepseek.png',
  'Kimi': '/icons/kimi.png',
  'kimi': '/icons/kimi.png',
  '元宝': '/icons/yuanbao.png',
  '腾讯元宝': '/icons/yuanbao.png',
  '百度AI搜索': '/icons/baidu.png',
  '百度': '/icons/baidu.png',
  '小红书AI': '/icons/xiaohongshu.png',
  '抖音AI': '/icons/douyin.png',
  '小红书': '/icons/xiaohongshu.png',
  'xhs': '/icons/xiaohongshu.png',
  '微信搜一搜': '/icons/wechat.png',
  '微信': '/icons/wechat.png',

                                'tongyi': '/icons/tongyi.png',
                                'wenxin': '/icons/wenxin.png',
                                'yuanbao': '/icons/yuanbao.png',
                                'baidu_ai': '/icons/baidu.png',
                                'baidu': '/icons/baidu.png',
                                'xiaohongshu': '/icons/xiaohongshu.png',
                                'xiaohongshu_seo': '/icons/xiaohongshu.png',
                                'wechat': '/icons/wechat.png',
                                'douyin': '/icons/douyin.png',
                                'zhihu': '/icons/zhihu.png',
                                'bilibili': '/icons/bilibili.png',
                                'toutiao': '/icons/toutiao.png',
                                'weibo': '/icons/weibo.png',
                            };
                            const iconSrc = iconMap[e.engineId] || '/vite.svg';
                            
                            return (
                              <div 
                                key={e.id} 
                                className="relative group flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors shadow-sm cursor-help"
                              >
                                <img 
                                  src={iconSrc} 
                                  alt={e.name} 
                                  className="w-5 h-5 object-contain" 
                                  onError={(ev) => { ev.target.style.display = 'none'; }}
                                />
                                {(e.engineId === 'doubmb' || e.engineId === 'ybmb' || e.engineId === 'qwmb') && (
                                  <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-800 rounded-full p-[1px] shadow-sm border border-gray-200 dark:border-slate-700">
                                    <svg className="w-2.5 h-2.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
                                  </div>
                                )}
                                {(e.engineId === 'doubao' || e.engineId === 'yuanbao' || e.engineId === 'tongyi') && (
                                  <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-800 rounded-full p-[1px] shadow-sm border border-gray-200 dark:border-slate-700">
                                    <svg className="w-2.5 h-2.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
                                  </div>
                                )}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block px-2.5 py-1 text-[11px] font-medium text-white bg-gray-900 dark:bg-gray-700 rounded whitespace-nowrap z-10 shadow-lg pointer-events-none transition-opacity">
                                  {e.name}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                                </div>
                              </div>
                            );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 dark:text-gray-500 italic">该项目尚未激活任何引擎</p>
                    )}
                  </div>

                  {/* (2) 监控计划情况 */}
                  <div className="pt-2">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-medium">监控计划情况</div>
                    <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${planClass}`}>
                      {planStatusLabel}
                    </span>
                  </div>
                  
                  {/* (3) 消耗积分情况 */}
                  <div className="pt-2">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-medium">消耗积分评估</div>
                    <div className="flex items-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                      <Zap size={16} className="mr-1.5 text-yellow-500" />
                      {isRecurring ? `约 ${estimatedCost} 次 / 月` : `单次抓取 ${estimatedCost} 次`}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 dark:border-slate-700 flex justify-between items-center text-xs text-gray-400 dark:text-gray-500">
                  <div>创建于 {new Date(project.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
